/* eslint-disable max-classes-per-file */
import { NgZone } from "@angular/core";
import { isNil } from "lodash";
import { Subject } from "rxjs";
import { CellChangeArg, Cellstyle, HotCellDropdown, HotCellText } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { ISelectValue } from "~app/common/models/selectValueModel";
import { VKNumericHotCell } from "~app/features/virkeskop/models/hotdoc/vk-numeric.hot-cell";
import { VKVirkeskostnaderHDCellStyles, Virkeskostnad } from "~app/features/virkeskop/models/virkeskostnader/virkeskostnader-models";

const parseFloatWithFormat = (value: string) =>
    parseFloat(value.replace(",", "."));

export class VirkeskostnadColumnTypes {
// eslint-disable-next-line sonarjs/public-static-readonly
    static dropdown = "dropdown";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static text = "text";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static numeric = "numeric";
}

export class VirkeskostnadCellNames {
// eslint-disable-next-line sonarjs/public-static-readonly
    static mattslag = "mattslag";
}

export type VirkeskostnadColumnDefinition = IHotColumnDefinition & {
    sumFunction?: (values: number[], col: VirkeskostnadColumnDefinition, tableData: Virkeskostnad[]) => number;
    sumText?: string;
    tooltip?: string;
    disabled?: (item: Virkeskostnad) => boolean;
    validateFn?: (value: any) => boolean;
    roundToInteger?: boolean;
};

export class VirkeskostnadTableBinder {
    hotDoc: HotDoc;
    cellStyles: Map<string, VKVirkeskostnaderHDCellStyles>;
    mattslag: ISelectValue[];

    private readonly modelChangeSrc = new Subject<Virkeskostnad>();
    get modelChange$() {
        return this.modelChangeSrc.asObservable();
    }

    private readonly mattslagChangeSrc = new Subject<Virkeskostnad>();
    get mattslagChange$() {
        return this.mattslagChangeSrc.asObservable();
    }

    constructor(
        private readonly columns: VirkeskostnadColumnDefinition[],
        private readonly ngZone: NgZone) { }

    rebind() {
        this.ngZone.runOutsideAngular(() => {
            this.hotDoc.rebind();
        });
    }

    initTable(tableData: Virkeskostnad[], mattslag: ISelectValue[]): void {
        const cols = [
            {
                header: "HS",
                cellName: "HS",
                readOnly: true,
                type: "text"
            },
            ...this.columns
        ];

        cols.forEach(c => {
            this.createHeaderWithTitle(c);
        });

        this.mattslag = mattslag;

        const colFactory = () => cols;

        const rowFactory = (doc: HotDoc) => this.rowFactory(doc, tableData);

        const settings: IHotDocSettings = {
            raskSettings: {
                calculateSum: false,
                numericParser: parseFloatWithFormat
            },
            stretchH: "all"
        };

        this.ngZone.runOutsideAngular(() => {
            this.hotDoc = new HotDoc(colFactory, rowFactory, () => settings);
        });
    }

    setCellStyles(inCellStyles: Map<string, VKVirkeskostnaderHDCellStyles>) {
        this.cellStyles = inCellStyles;
        this.rebind();
    }

    private createHeaderWithTitle(col: VirkeskostnadColumnDefinition) {
        if (col.tooltip) {
            col.header = `<span title='${col.tooltip}'>${col.header}</span>`;
        }
    }

    private rowFactory(doc: HotDoc, tableData: Virkeskostnad[]) {
        tableData
            .forEach(item => {
                doc.createRow(row => this.itemRowFactory(tableData, item, row));
            });

        doc.createRow(row => this.summaRowFactory(tableData, row));
    }

    private itemRowFactory(tableData: Virkeskostnad[], item: Virkeskostnad, row: HotRow) {
        const rowCellStyles = this.cellStyles?.get(item.handelssortimentNamn);

        if (!isNil(item.handelssortimentId)) {
            row.addText("HS", item.handelssortimentNamn, Cellstyle.Readonly);
        } else {
            // eslint-disable-next-line no-bitwise
            row.addText("HS", item.handelssortimentsgruppNamn, Cellstyle.Readonly | Cellstyle.Bold);
        }

        this.columns.forEach(c => {
            const cellStyle = rowCellStyles?.[c.cellName];
            this.createCell(row, item, c, cellStyle ?? null, c.validateFn);
        });
    }

    private createCell(row: HotRow, item: Virkeskostnad, col: VirkeskostnadColumnDefinition, customCellStyle: Cellstyle, validateFn?: (value: any) => boolean) {
        const callback = (change: CellChangeArg) => {
            item[col.cellName] = col.type === VirkeskostnadColumnTypes.dropdown ? change.rawNewValue : change.newValue;
            if (col.cellName === VirkeskostnadCellNames.mattslag) {
                this.mattslagChangeSrc.next(item);
            }
            change.complete();
            this.modelChangeSrc.next(item);
        };

        let cellStyle = Cellstyle.Default;
        if (col.readOnly === true) {
            cellStyle = Cellstyle.Readonly;
        } else if ((col.disabled && col.disabled(item))) {
            cellStyle = Cellstyle.Readonly;
        } else if (item.kvantitet === item.utbyteInlastKvantitet && item.kvantitet !== 0 && col.cellName === "kvantitet") {
            cellStyle = Cellstyle.Italic;
        } else if (customCellStyle) {
            cellStyle = customCellStyle;
        }

        const formatOptions = {
            roundingMode: "halfExpand", // default
            maximumFractionDigits: col.roundToInteger === true ? 0 : 20
        } as Intl.NumberFormatOptions;

        const hotCell = this.getHotCell(item, col, cellStyle, callback, formatOptions, validateFn);
        row.add(col.cellName, hotCell);
    }

    private summaRowFactory(tableData: Virkeskostnad[], row: HotRow) {

        row.add("HS", new HotCellText("Summa", Cellstyle.Readonly, null));
        for (const column of this.columns) {
            const formatOptions = {
                roundingMode: "halfExpand", // default
                maximumFractionDigits: column.roundToInteger === true ? 0 : 20
            } as Intl.NumberFormatOptions;

            if (!column.sumFunction) {
                const sumText = column.sumText;
                row.add(column.cellName, new HotCellText(sumText, Cellstyle.Readonly));
            } else {
                const values = tableData.map(item => item[column.cellName] as number);
                const sum = column.sumFunction(values, column, tableData);
                const cellstyle = Cellstyle.Readonly;
                row.add(column.cellName, new VKNumericHotCell(sum, [], cellstyle, null, formatOptions));
            }
        }
    }

    private getHotCell(
        item: Virkeskostnad,
        col: VirkeskostnadColumnDefinition,
        cellStyle: Cellstyle,
        callback: (change: CellChangeArg) => void,
        formatOptions: Intl.NumberFormatOptions,
        validateFn?: (value: any) => boolean): any {

        switch (col.type) {
            case VirkeskostnadColumnTypes.text:
                return new HotCellText(item[col.cellName], cellStyle);
            case VirkeskostnadColumnTypes.dropdown:
                return new HotCellDropdown(item[col.cellName], cellStyle, null, callback);
            default:
                return new VKNumericHotCell(item[col.cellName], [], cellStyle, callback, formatOptions, validateFn);
        }
    }
}
